import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import api from '../utils/api';
import { API_URL, CIP_APP_URL, CIP_RSMIN_TENANT_SLUG } from '../components/config';
import HarvestingTopNav from '../components/HarvestingTopNav';
import { getViewAsMode } from '../utils/viewMode';
import './HarvestingStaticPage.css';

const HarvestingStaticPage = () => {
  const { slug, parentSlug } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(null);
  const [children, setChildren] = useState([]);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [connectorConfig, setConnectorConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [expanded, setExpanded] = useState(() => new URLSearchParams(location.search).get('fullscreen') === '1');
  const viewAsMode = getViewAsMode(location.search);
  const previewMode = Boolean(viewAsMode);
  const previewQuery = viewAsMode ? `?viewAs=${viewAsMode}` : '';

  const activeSlug = slug;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setExpanded(params.get('fullscreen') === '1');
  }, [location.search]);

  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      setError('');
      try {
        const response = previewMode
          ? await axios.get(`${API_URL}/api/harvesting-pages/${activeSlug}`)
          : await api.get(`/api/harvesting-pages/${activeSlug}`);
        setPage(response.data.page);
        setChildren(response.data.children || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Page not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPage();
  }, [activeSlug, previewMode]);

  useEffect(() => {
    if (!activeSlug) return;
    const fetchPosts = async () => {
      try {
        const response = previewMode
          ? await axios.get(`${API_URL}/api/harvesting-posts/public?pageSlug=${activeSlug}`)
          : await api.get(`/api/harvesting-posts/public?pageSlug=${activeSlug}`);
        setPosts(response.data.posts || []);
      } catch (err) {
        // Non-fatal: page content still renders without topic posts
        setPosts([]);
      }
    };
    fetchPosts();
  }, [activeSlug, previewMode]);

  useEffect(() => {
    if (!page?.embedCipReport || previewMode) {
      setConfigLoading(false);
      return;
    }
    const fetchConnectorConfig = async () => {
      setConfigLoading(true);
      try {
        const res = await api.get('/api/connector/config');
        setConnectorConfig(res.data?.config || null);
      } catch {
        setConnectorConfig(null);
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConnectorConfig();
  }, [page?.embedCipReport, previewMode]);

  const authToken = localStorage.getItem('authToken');
  const embedCfg = connectorConfig?.embedSettings || {};
  const integrationMode = embedCfg.integrationMode === 'live' ? 'live' : 'testing';
  const cipIntegrationEnabled = Boolean(embedCfg.cipIntegrationEnabled);
  const cipConfiguredBase = String(embedCfg.cipBaseUrl || CIP_APP_URL || '').replace(/\/$/, '');
  const cipReady = !!cipConfiguredBase;
  const canBridge = cipReady && !!authToken;
  const nextPathLog = `/t/${CIP_RSMIN_TENANT_SLUG}/harvesting?embedded=1&embedHost=rs&mode=contribute&tab=submit&linkMode=${integrationMode}`;
  const cipLogUrl = canBridge
    ? `${cipConfiguredBase}/auth/rsmin-callback?embedded=1&token=${encodeURIComponent(authToken)}&next=${encodeURIComponent(nextPathLog)}`
    : '';
  const showCipEmbed = Boolean(page?.embedCipReport) && cipIntegrationEnabled && !configLoading;
  const fullscreen = showCipEmbed && canBridge && expanded;

  useEffect(() => {
    if (!fullscreen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKey = (event) => { if (event.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', handleKey);
    };
  }, [fullscreen]);

  const stripHtml = (value) => {
    if (!value) return '';
    return value.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  };

  if (loading) {
    return (
      <div className={`harvesting-static-page${showCipEmbed ? ' harvesting-static-page--tool' : ''}`}>
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="harvesting-static-page">
        <Link to={`/harvesting${previewQuery}`} className="back-link">← Back to Harvesting Portal</Link>
        <div className="error-message">{error || 'Page not found'}</div>
      </div>
    );
  }

  return (
    <div className={`harvesting-static-page${showCipEmbed ? ' harvesting-static-page--tool' : ''}`}>
      <HarvestingTopNav />

      <div className="static-page-header">
        {parentSlug ? (
          <Link to={`/harvesting/pages/${parentSlug}${previewQuery}`} className="back-link">← Back</Link>
        ) : (
          <Link to={`/harvesting${previewQuery}`} className="back-link">← Back to Harvesting Portal</Link>
        )}
        <h1>{page.title}</h1>
        {page.subtitle && <p className="subtitle">{page.subtitle}</p>}
      </div>

      <div className="static-page-content" dangerouslySetInnerHTML={{ __html: page.content || '' }} />

      {page.attachments && page.attachments.length > 0 && (
        <div className="static-page-attachments">
          {page.attachments.map((attachment, index) => (
            <a
              key={index}
              href={attachment.url}
              target="_blank"
              rel="noopener noreferrer"
              className="static-page-attachment-btn"
            >
              {attachment.buttonLabel || attachment.filename}
            </a>
          ))}
        </div>
      )}

      {showCipEmbed && (
        <div className={`cip-embed-panel cip-harvest-tool${fullscreen ? ' cip-harvest-tool--fullscreen' : ''}`}
          role="region" aria-label="RSMIN harvest reporting">
          {!canBridge && (
            <div className="cip-embed-warning">
              {cipReady
                ? 'Please log in to open embedded CIP harvesting tools.'
                : 'CIP integration is enabled but CIP base URL is not configured yet in Integrations settings.'}
            </div>
          )}

          {canBridge && (
            <>
              <div className="cip-embed-actions">
                <span>Log a harvest</span>
                <button type="button" className="tile-button secondary" onClick={() => setExpanded((value) => !value)} aria-expanded={fullscreen}>
                  {fullscreen ? 'Exit full screen' : 'Full screen'}
                </button>
                {fullscreen && <Link to="/harvesting" className="tile-button secondary">Back to Harvesting Portal</Link>}
              </div>
              <div className="cip-embed-frame-wrap">
                <iframe
                  title="CIP Harvesting Embedded"
                  src={cipLogUrl}
                  className="cip-embed-frame"
                  loading="eager"
                  referrerPolicy="strict-origin-when-cross-origin"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                />
              </div>
            </>
          )}
        </div>
      )}

      {children.length > 0 && (
        <div className="static-page-children">
          <h2>In This Section</h2>
          <div className="static-page-children-list">
            {children.map((child) => (
              <Link
                key={child._id}
                to={`/harvesting/pages/${page.slug}/${child.slug}${previewQuery}`}
                className="static-page-child-link"
              >
                {child.title}
              </Link>
            ))}
          </div>
        </div>
      )}

      {posts.length > 0 && (
        <div className="static-page-posts">
          <h2>Updates</h2>
          <div className="blog-post-cards">
            {posts.map((post) => (
              <div key={post._id} className="blog-post-card">
                {post.imageUrl && (
                  <div className="blog-post-image">
                    <img src={post.imageUrl} alt={post.title} />
                  </div>
                )}
                <div className="post-date">
                  {new Date(post.publishDate).toLocaleDateString('en-US', {
                    timeZone: 'America/Toronto',
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <h3>{post.title}</h3>
                <p className="post-excerpt">
                  {stripHtml(post.content).length > 160
                    ? stripHtml(post.content).substring(0, 160) + '...'
                    : stripHtml(post.content)}
                </p>
                <Link to={`/harvesting/blog/${post._id}${previewQuery}`} className="read-more-link">
                  Read More →
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HarvestingStaticPage;
